import { useMutation, useQuery } from '@apollo/client';
import {
  Company,
  CompanyType,
  ErrorResponse,
  GET_COMPANY,
  PATCH_COMPANY,
  UploadedFile,
} from '@frontend/data-access';
import { ImageUpload } from '@frontend/shared/ui';
import { App, Button, Form, Input, Select, Spin, UploadFile } from 'antd';
import useTranslation from 'next-translate/useTranslation'; // Added translation import
import { useEffect, useMemo, useState } from 'react';

interface FormValues {
  name: string;
  logo: UploadedFile;
  companyType: CompanyType;
}

function EditInformation({
  companyId,
  onFinish,
  onCancel,
}: {
  companyId: number;
  onFinish: () => void;
  onCancel: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const { data, loading } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: companyId },
  });
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [form] = Form.useForm();

  const companyType = useMemo(
    () => [
      {
        label: t('company_type_accelerator'),
        value: 'ACCELERATOR',
      },
      {
        label: t('company_type_consultancy'),
        value: 'CONSULTANCY',
      },
      {
        label: t('company_type_corporation'),
        value: 'CORPORATE',
      },
      {
        label: t('company_type_government'),
        value: 'GOVERNMENT',
      },
      {
        label: t('company_type_startup'),
        value: 'STARTUP',
      },
      {
        label: t('company_type_research'),
        value: 'RESEARCH',
      },
      {
        label: t('company_type_investment'),
        value: 'INVESTMENT',
      },
    ],
    [t]
  );

  const handleCompleted = () => {
    notification.success({
      message: t('success_message'),
    });
    onFinish();
  };

  const handleError = (data: ErrorResponse) => {
    notification.error({
      message: t('error_message'),
      description: data.message,
    });
  };

  const [patchCompany, { loading: loadingPatch }] = useMutation(PATCH_COMPANY, {
    onCompleted: handleCompleted,
    onError: handleError,
  });

  const handleOnFinish = (values: FormValues) => {
    patchCompany({
      variables: {
        input: {
          id: companyId,
          name: values.name,
          logo: values.logo,
          companyType: values.companyType,
        },
      },
    });
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsIncomplete(hasErrors);
  };

  useEffect(() => {
    if (data?.company) {
      form.setFieldsValue({
        name: data?.company.name,
        logo: data?.company.logo,
        companyType: data?.company.companyType,
      });
      setFileList([
        {
          url: data.company.logo?.url,
          uid: data.company.logo?.id as string,
          name: data.company.logo?.id as string,
        },
      ]);
    }
  }, [data, form]);

  if (loading) {
    return (
      <div className="p-6 text-center">
        <Spin />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleOnFinish}
      onFieldsChange={handleFormChange}
    >
      <Form.Item name="logo" label={t('institution_logo')}>
        <ImageUpload
          data-test="avatar-upload"
          title={t('upload_title')}
          description={t('upload_requirements')}
          crop={{ aspect: 1 }}
          onUploadSuccess={(v) => form.setFieldValue('logo', v)}
          // onRemove={onRemove}
          maxFileSize={1024 * 1024 * 5} // 5MB
          defaultImages={fileList}
          onRemove={() => form.setFieldValue('logo', null)}
        />
      </Form.Item>
      <Form.Item
        name="name"
        label={t('institution_name')}
        rules={[{ required: true, message: t('input_name') }]}
      >
        <Input
          placeholder={t('institution_name_placeholder')}
          maxLength={200}
        />
      </Form.Item>
      <Form.Item
        name="companyType"
        label={t('institution_type')}
        rules={[{ required: true, message: t('company_type_required') }]}
      >
        <Select placeholder={t('select_company_type')}>
          {companyType.map((item) => (
            <Select.Option key={item.value} value={item.value}>
              {item.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <div className="flex justify-end items-center gap-2">
        <Button disabled={loadingPatch} shape="round" onClick={onCancel}>
          {t('cancel_button')}
        </Button>
        <Button
          htmlType="submit"
          type="primary"
          loading={loadingPatch}
          disabled={isIncomplete}
          shape="round"
        >
          {t('save_button')}
        </Button>
      </div>
    </Form>
  );
}

export default EditInformation;
